import { Component, type ReactElement } from "react";

import { getHitQueryRange } from "../../app-utils/blast.js";
import { rangesIntersect } from "../../app-utils/range.js";
import { type IPileup, type IPileupHit } from "../../app-utils/track-shapes/track-pileup-factory.js";
import { type BlastHit, type BlastHsp, type Range } from "../../graphql.js";
import { type IBoxModel } from "../genome-viewer/interfaces.js";
import { HitHspChainShape } from "./HitHspChainShape.js";

export interface IHitsTrackProps {
  boxModel: IBoxModel;
  range: Range; // query sequence range in model coordinates [bases] based at 1
  pileup: IPileup;
  onHitClick: (hit: BlastHit, hsp: BlastHsp) => void;
}

/**
 * A visualization component that renders the piled hits of a BLAST search result.
 *
 * The component is expected to be used within an existing SVG context and does NOT create its own SVG element. The
 * main input data is pileup hit whose view coordinates and a width have already been computed elsewhere.
 *
 * created on 4/18/2017
 */
class HitsTrack extends Component<IHitsTrackProps> {
  render() {
    const pileup: IPileup = this.props.pileup;
    const bm: IBoxModel = this.props.boxModel;

    const height: number = this.getTrackHeight(pileup, bm);
    const contentWidth: number = bm.width - bm.padding.left - bm.padding.right;

    const translateContent = `translate(${bm.padding.left},${bm.padding.top})`;
    const clipPathId = "clip-blast-hits";

    const contents: ReactElement[] = this.renderPileup(pileup);

    return (
      <g className="hits-track">
        <g className="hits-track-content" transform={translateContent}>
          <defs>
            <clipPath id={`${clipPathId}`}>
              <rect id="clip-rect" x="0" y={-bm.padding.top} width={contentWidth + 1} height={height} />
            </clipPath>
          </defs>
          <g clipPath={`url(#${clipPathId})`}>{contents}</g>
        </g>

        <line
          className="separator-line"
          x1={bm.padding.left}
          y1={height - 0.5}
          x2={bm.padding.left + contentWidth}
          y2={height - 0.5}
        />
      </g>
    );
  }

  renderPileup = (pileup: IPileup): ReactElement[] => {
    const result: ReactElement[] = [];
    const currentRange: Range = this.props.range;

    if (pileup.hits) {
      const featureCount: number = pileup.hits.length;
      for (let i = 0; i < featureCount; i++) {
        const pileupHit: IPileupHit = pileup.hits[i];
        if (rangesIntersect(getHitQueryRange(pileupHit.hit), currentRange)) {
          result.push(
            <HitHspChainShape
              hit={pileupHit}
              onClick={this.props.onHitClick}
              key={`hit-shape-${pileupHit.hit.rank}`}
            />,
          );
        }
      }
    }
    return result;
  };

  getTrackHeight = (pileup: IPileup, bm: IBoxModel) => {
    const { lanes, pileupParams } = pileup;
    const { featureHeight, labelHeight, lanePadding } = pileupParams;
    const pileupHeight = Math.max(lanes, 1) * (featureHeight + labelHeight) + (lanes - 1) * lanePadding;

    return pileupHeight + bm.padding.top + bm.padding.bottom;
  };
}

export { HitsTrack };
